import _ from 'underscore';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Dropdown, SearchBox, SelectableOptionMenuItemType, DirectionalHint } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { getStyles } from './styles';

const RESERVED_SEARCH_KEY = 'search-key-c6581d02-80fb-4657-bc8d-95850a15a868';

const getSearchableOptions = (initialOptions) => {
  if (_.isEmpty(initialOptions)) {
    return initialOptions;
  }

  const optionsWithSearch = [];
  optionsWithSearch.push({
    key: RESERVED_SEARCH_KEY,
    text: '',
    itemType: SelectableOptionMenuItemType.Header,
  });

  return optionsWithSearch.concat([...initialOptions]);
};

export function SearchableDropdown(props) {
  const {
    options,
  } = props;
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);

  const searchableOptions = useMemo(() => getSearchableOptions(options), [options]);
  const [filteredOptions, setFilteredOptions] = useState(searchableOptions);

  const onSearchValueChanged = useCallback((searchValue) => {
    if (_.isEmpty(searchValue)) {
      setFilteredOptions(searchableOptions);
      return;
    }

    const matchedOptions = _.filter(
      searchableOptions,
      option => (option.key === RESERVED_SEARCH_KEY || option.text.toLowerCase().includes(searchValue.toLowerCase()))
    );

    setFilteredOptions(matchedOptions);
  }, [searchableOptions]);

  const onChangeSearchBox = useCallback((event, searchValue) => {
    onSearchValueChanged(searchValue);
  }, [onSearchValueChanged]);

  const onClearSearchBox = useCallback(() => {
    onSearchValueChanged('');
  }, [onSearchValueChanged]);

  const onRenderOption = useCallback((option = {}, renderFunc = _.noop) => {
    if (option.key === RESERVED_SEARCH_KEY) {
      return (
        <SearchBox
          className={classNames('searchable-dropdown-search-box', classes.searchableDropdownSearchBox)}
          placeholder={i18n.getString(_TL_('Search'))}
          onChange={_.debounce(onChangeSearchBox, 100)}
          onSearch={onSearchValueChanged}
          onClear={onClearSearchBox}
          autoFocus
        />
      );
    }

    return renderFunc(option);
  }, [classes.searchableDropdownSearchBox, i18n, onChangeSearchBox, onClearSearchBox, onSearchValueChanged]);

  useEffect(() => setFilteredOptions(getSearchableOptions(options)), [options]);

  return (
    <div className={classNames('searchable-dropdown-container', classes.searchableDropdownContainer)}>
      <Dropdown
        {...props}
        options={filteredOptions}
        onRenderOption={onRenderOption}
        onDismiss={onClearSearchBox}
        calloutProps={{ directionalHint: DirectionalHint.bottomCenter, directionalHintFixed: true }}
      />
    </div>
  );
}

SearchableDropdown.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, text: PropTypes.string })),
};

SearchableDropdown.defaultProps = {
  options: [],
};
